"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.seoTechnicalAudit = exports.seoTechnicalAuditResultSchema = exports.seoTechnicalAuditArgsSchema = exports.urlSeoTechnicalAuditResultSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// Shared options schema
var optionsSchema = zod_1.z
    .object({
    failIfEmpty: zod_1.z
        .boolean()
        .default(true)
        .describe('Fail if no URLs are found to audit. Default to true.'),
})
    .describe('Audit options');
// Base URL audit result schema (shared between both actions)
exports.urlSeoTechnicalAuditResultSchema = zod_1.z.object({
    source_url: zod_1.z.string().describe('The URL that was audited'),
    link_text: zod_1.z
        .string()
        .describe('Helps search engines understand content through descriptive text'),
    hreflang: zod_1.z
        .string()
        .describe('Specifies language/region-specific page versions for search results'),
    image_alt: zod_1.z
        .string()
        .describe('Use brief descriptions for informative images; decorative ones can be empty'),
    robots_txt: zod_1.z.string().describe('Guides crawlers; malformed files may hinder proper indexing'),
    crawlable_anchors: zod_1.z
        .string()
        .describe('Ensures href links lead to discoverable pages on the site'),
    is_crawlable: zod_1.z.string().describe('Allows search engines to index pages with crawl permissions'),
    canonical: zod_1.z.string().describe('Suggests preferred URL to display in search results'),
    meta_description: zod_1.z.string().describe('Provides a concise page summary for search results'),
    http_status_code: zod_1.z.string().describe('Non-success codes may prevent proper indexing'),
    document_title: zod_1.z
        .string()
        .describe('Titles give an overview and aid relevance in search results'),
    mobile_pagespeed_score: zod_1.z.number().describe('Google PageSpeed score for mobile devices (0-100)'),
    mobile_optimized_score: zod_1.z.number().describe('Optimization score for mobile devices'),
    desktop_pagespeed_score: zod_1.z
        .number()
        .describe('Google PageSpeed score for desktop devices (0-100)'),
    desktop_optimized_score: zod_1.z.number().describe('Optimization score for desktop devices'),
});
// Action-specific args schemas
var auditUrlArgsSchema = zod_1.z.object({
    action: zod_1.z.literal('AuditURL').describe('Audit a single URL'),
    url: zod_1.z
        .string()
        .describe('The URL to perform technical SEO audit on. Supports flexible URL formats - protocol is optional and will be auto-normalized. Examples: "https://example.com", "example.com", "example.com/page", "www.example.com/article".'),
    emitMode: common_1.emitModeSchema.default('single').describe('The mode to emit the data in.'),
    options: optionsSchema,
});
var auditSiteArgsSchema = zod_1.z.object({
    action: zod_1.z.literal('AuditSite').describe('Audit an entire website'),
    url: zod_1.z
        .string()
        .describe('The domain to audit. Supports flexible URL formats - protocol is optional and will be auto-normalized. Examples: "https://example.com", "example.com", "www.example.com", "blog.example.com".'),
    crawlMethod: zod_1.z
        .enum(['Sitemap', 'Internal Crawl', 'Both'])
        .default('Sitemap')
        .describe('Crawl method: "Sitemap" uses sitemap.xml only, "Internal Crawl" crawls internal links up to 4 levels deep, "Both" combines both methods'),
    maxLinks: zod_1.z
        .number()
        .int()
        .min(-1)
        .default(50)
        .describe('Maximum number of URLs to audit. Use -1 for unlimited. Default is 50.'),
    emitMode: common_1.emitModeSchema.default('single').describe('The mode to emit the data in.'),
    options: optionsSchema,
});
// Discriminated union for args
exports.seoTechnicalAuditArgsSchema = zod_1.z.discriminatedUnion('action', [
    auditUrlArgsSchema,
    auditSiteArgsSchema,
]);
// Action-specific result schemas
var auditUrlResultSchema = exports.urlSeoTechnicalAuditResultSchema;
var auditSiteResultSchema = zod_1.z.object({
    domain_created_date: zod_1.z.string().optional().describe('Domain creation date in YYYY-MM-DD format'),
    url_audits: zod_1.z
        .array(exports.urlSeoTechnicalAuditResultSchema)
        .describe('Array of technical SEO audit results for each discovered URL'),
});
// Dynamic result schemas based on action
exports.seoTechnicalAuditResultSchema = zod_1.z.discriminatedUnion('action', [
    zod_1.z.object({
        action: zod_1.z.literal('AuditURL'),
        result: auditUrlResultSchema,
    }),
    zod_1.z.object({
        action: zod_1.z.literal('AuditSite'),
        result: auditSiteResultSchema,
    }),
]);
exports.seoTechnicalAudit = (0, common_1.createToolMetadata)({
    id: 'seoTechnicalAudit',
    displayName: 'SEO Technical Audit',
    displayDescription: 'Perform comprehensive technical SEO audits on URLs or entire websites. AuditURL analyzes a single page, while AuditSite crawls multiple pages via sitemap and/or internal links. Analyzes link text, hreflang, image alt tags, robots.txt, crawlability, canonical URLs, meta descriptions, HTTP status codes, document titles, and PageSpeed scores. Supports flexible URL formats with auto-protocol detection.',
    category: 'search',
    color: common_1.BRAND_COLORS.WEB,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.WEB),
    icon: 'search',
    argsSchema: exports.seoTechnicalAuditArgsSchema,
}, exports.seoTechnicalAuditResultSchema);
